/**
 * Stat card — a single number + label, used in the "stats strip" pattern
 * at the top of V2 list pages (Properties / Total rooms / Occupied).
 *
 * Visually: large numeric value, small caption above, optional sub-line
 * below for trend / ratio. Loading state fades the value to a skeleton.
 */
import { ReactNode } from 'react'
import { LucideIcon } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

type Props = {
  label: string
  value: ReactNode
  /** Subtle caption rendered under the value — e.g. "of 92 total". */
  hint?: string
  icon?: LucideIcon
  /** When true, the value cell renders a skeleton instead. */
  loading?: boolean
  className?: string
}

export function StatCard({ label, value, hint, icon: Icon, loading, className }: Props) {
  return (
    <Card className={`p-4 ${className ?? ''}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {label}
          </p>
          {loading ? (
            <Skeleton className="h-7 w-16" />
          ) : (
            <p className="text-2xl font-semibold tracking-tight tabular-nums">{value}</p>
          )}
          {hint && !loading && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
        {Icon && (
          <div className="rounded-md bg-muted p-2 text-muted-foreground">
            <Icon className="h-4 w-4" />
          </div>
        )}
      </div>
    </Card>
  )
}
